import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Chip, FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Divider,
  Popover, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Clear as ClearIcon, RestoreFromTrash as RestoreIcon, Visibility as ViewIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { Decaissement, MOTIF_LABELS, STATUS_LABELS, BENEFICIAIRE_TYPE_LABELS, PAIEMENT_TYPE_LABELS } from '../../types/decaissement';
import { Projet } from '../../types/projet';
import { ModePaiement } from '../../types/modePaiement';
import { Supplier } from '../../types/supplier';
import { Beneficiaire } from '../../types/beneficiaire';
import { decaissementsAPI, projetsAPI, modesPaiementAPI, suppliersAPI, beneficiairesAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import DecaissementFormDialog from './DecaissementFormDialog';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';

const FINANCIER_COLOR = '#9C27B0';

const DECAISSEMENT_FILTER_SCHEMA = {
  date:               { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  echeance:           { type: 'dateRange',    paramFrom: 'echeance_from', paramTo: 'echeance_to' },
  projet_id:          { type: 'autocomplete', param: 'projet_id', transform: (v: string) => parseInt(v) },
  beneficiaire_type:  { type: 'select',       param: 'beneficiaire_type' },
  fournisseur_id:     { type: 'autocomplete', param: 'fournisseur_id', transform: (v: string) => parseInt(v) },
  beneficiaire_id:    { type: 'autocomplete', param: 'beneficiaire_id', transform: (v: string) => parseInt(v) },
  libelle:            { type: 'text',         param: 'libelle' },
  piece_justificatif: { type: 'text',         param: 'piece_justificatif' },
  mode_paiement_id:   { type: 'select',       param: 'mode_paiement_id', transform: (v: string) => parseInt(v) },
  reference_paiement: { type: 'text',         param: 'reference_paiement' },
  montant:            { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max', transform: (v: string) => parseFloat(v) },
} as const;

const DecaissementList: React.FC = () => {
  const [decaissementsList, setDecaissementsList] = useState<Decaissement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedList, setDeletedList] = useState<Decaissement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [motifFilter, setMotifFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<Decaissement | null>(null);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    DECAISSEMENT_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [beneficiaryFilterAnchor, setBeneficiaryFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const [projetFilterAnchor, setProjetFilterAnchor] = useState<HTMLButtonElement | null>(null);

  // Options for select filters
  const [projets, setProjets] = useState<Projet[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);
  const [fournisseurs, setFournisseurs] = useState<Supplier[]>([]);
  const [beneficiaires, setBeneficiaires] = useState<Beneficiaire[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [projetsData, modesData, fournisseursData, beneficiairesData] = await Promise.all([
          projetsAPI.getAllForDropdown(),
          modesPaiementAPI.getAll(),
          suppliersAPI.getAllForDropdown(),
          beneficiairesAPI.getAll(),
        ]);
        setProjets(projetsData);
        setModesPaiement(modesData);
        setFournisseurs(fournisseursData);
        setBeneficiaires(beneficiairesData);
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchOptions();
  }, []);

  const fetchDecaissements = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;

      // projet_id and beneficiaire_type go positionally; everything else into the filters bag
      const { projet_id, beneficiaire_type, ...filterBag } = toQueryParams();

      const response = await decaissementsAPI.getPaginated(
        skip, rowsPerPage, debouncedSearch || undefined,
        (projet_id as number | undefined),
        (beneficiaire_type as string | undefined),
        motifFilter || undefined, statusFilter || undefined,
        Object.keys(filterBag).length > 0 ? filterBag : undefined
      );
      setDecaissementsList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des décaissements');
      setDecaissementsList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, motifFilter, statusFilter, filters]);

  const fetchDeleted = async () => {
    try {
      setLoading(true);
      const data = await decaissementsAPI.getDeleted();
      setDeletedList(data);
    } catch (err) {
      showError('Erreur lors du chargement des décaissements supprimés');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) fetchDecaissements();
  }, [fetchDecaissements, tabValue]);

  // Reset page when motif/status sub-filters change (column filters reset page via the hook)
  useEffect(() => { setPage(0); }, [motifFilter, statusFilter]);

  // Get beneficiary filter options based on selected type
  const getBeneficiaryOptions = () => {
    if (filters.beneficiaire_type === 'fournisseur') {
      return fournisseurs.map(f => ({ value: f.id.toString(), label: f.name }));
    } else if (filters.beneficiaire_type === 'beneficiaire') {
      return beneficiaires.map(b => ({ value: b.id.toString(), label: b.name }));
    }
    return [];
  };

  // Handle beneficiary type change - reset the specific ID when type changes
  const handleBeneficiaryTypeChange = (newType: string) => {
    update('beneficiaire_type', newType);
    update('fournisseur_id', '');
    update('beneficiaire_id', '');
  };

  // Handle specific beneficiary selection
  const handleBeneficiarySelect = (id: string) => {
    if (filters.beneficiaire_type === 'fournisseur') {
      update('fournisseur_id', id);
      update('beneficiaire_id', '');
    } else if (filters.beneficiaire_type === 'beneficiaire') {
      update('beneficiaire_id', id);
      update('fournisseur_id', '');
    }
  };

  // Get current beneficiary filter value
  const getCurrentBeneficiaryValue = () => {
    if (filters.beneficiaire_type === 'fournisseur') {
      return filters.fournisseur_id;
    } else if (filters.beneficiaire_type === 'beneficiaire') {
      return filters.beneficiaire_id;
    }
    return '';
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) fetchDeleted();
  };

  const handleDelete = async (item: Decaissement) => {
    showConfirm(`Êtes-vous sûr de vouloir supprimer le décaissement "${item.reference}" ?`, async () => {
      try {
        await decaissementsAPI.delete(item.id);
        showSuccess('Décaissement supprimé avec succès');
        fetchDecaissements();
      } catch (err: any) {
        showError(extractErrorMessage(err));
      }
    });
  };

  const handleRestore = async (item: Decaissement) => {
    showConfirm(`Êtes-vous sûr de vouloir restaurer "${item.reference}" ?`, async () => {
      try {
        await decaissementsAPI.restore(item.id);
        showSuccess('Décaissement restauré avec succès');
        fetchDeleted();
        fetchDecaissements();
      } catch (err: any) {
        showError(extractErrorMessage(err));
      }
    });
  };

  const handleAdd = () => { setEditId(undefined); setFormMinimized(false); setFormDialogOpen(true); };
  const handleEdit = (item: Decaissement) => { setEditId(item.id); setFormMinimized(false); setFormDialogOpen(true); };
  const handleFormSuccess = () => { setFormDialogOpen(false); setFormMinimized(false); fetchDecaissements(); };
  const handlePreview = (item: Decaissement) => { setPreviewItem(item); setPreviewOpen(true); };

  const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'brouillon': return 'warning';
      case 'validee': return 'success';
      case 'annulee': return 'error';
      default: return 'default';
    }
  };

  const getBeneficiaryName = (item: Decaissement): string => {
    return item.beneficiaire_type === 'fournisseur'
      ? item.fournisseur_name || '-'
      : item.beneficiaire_name || '-';
  };

  const formatAmount = (amount: number) => new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">Gestion des Décaissements</Typography>
        <PermissionButton moduleName="Financier" operation="add" miniModuleName="Decaissements" variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Nouveau Décaissement
        </PermissionButton>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Décaissements actifs" />
        <Tab label="Corbeille" />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Rechercher..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon /></IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Motif</InputLabel>
          <Select value={motifFilter} label="Motif" onChange={(e) => setMotifFilter(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="achat">Achat</MenuItem>
            <MenuItem value="matiere_premiere">Matière Première</MenuItem>
            <MenuItem value="achat_service">Achat de Service</MenuItem>
            <MenuItem value="avance_salaire">Avance sur Salaire</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={statusFilter} label="Statut" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="brouillon">Brouillon</MenuItem>
            <MenuItem value="validee">Validée</MenuItem>
            <MenuItem value="annulee">Annulée</MenuItem>
          </Select>
        </FormControl>
        {hasActive() && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={reset}
            sx={{ color: FINANCIER_COLOR }}
          >
            Effacer filtres colonnes
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={getModuleTableTheme('financier')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date
                  <ColumnFilter
                    type="dateRange"
                    value={filters.date}
                    onChange={(val) => update('date', val)}
                    label="Date"
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Échéance
                  <ColumnFilter
                    type="dateRange"
                    value={filters.echeance}
                    onChange={(val) => update('echeance', val)}
                    label="Échéance"
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Projet
                  <IconButton
                    size="small"
                    onClick={(e) => setProjetFilterAnchor(e.currentTarget)}
                    sx={{
                      ml: 0.5,
                      p: 0.25,
                      color: filters.projet_id ? FINANCIER_COLOR : 'inherit',
                      opacity: filters.projet_id ? 1 : 0.5,
                      '&:hover': { opacity: 1, color: FINANCIER_COLOR },
                    }}
                  >
                    <FilterIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Popover
                    open={Boolean(projetFilterAnchor)}
                    anchorEl={projetFilterAnchor}
                    onClose={() => setProjetFilterAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 2, minWidth: 280 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        Filtrer par Projet
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={projets.map(p => ({ value: p.id.toString(), label: p.nom }))}
                        getOptionLabel={(option) => option.label}
                        value={projets.map(p => ({ value: p.id.toString(), label: p.nom })).find(o => o.value === filters.projet_id) || null}
                        onChange={(_, newValue) => update('projet_id', newValue?.value || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Projet"
                            placeholder="Rechercher..."
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                      />
                    </Box>
                  </Popover>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Bénéficiaire
                  <IconButton
                    size="small"
                    onClick={(e) => setBeneficiaryFilterAnchor(e.currentTarget)}
                    sx={{
                      ml: 0.5,
                      p: 0.25,
                      color: (filters.beneficiaire_type || filters.fournisseur_id || filters.beneficiaire_id) ? FINANCIER_COLOR : 'inherit',
                      opacity: (filters.beneficiaire_type || filters.fournisseur_id || filters.beneficiaire_id) ? 1 : 0.5,
                      '&:hover': { opacity: 1, color: FINANCIER_COLOR },
                    }}
                  >
                    <FilterIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Popover
                    open={Boolean(beneficiaryFilterAnchor)}
                    anchorEl={beneficiaryFilterAnchor}
                    onClose={() => setBeneficiaryFilterAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 2, minWidth: 280 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        Filtrer par Bénéficiaire
                      </Typography>

                      {/* Step 1: Select Type */}
                      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={filters.beneficiaire_type}
                          label="Type"
                          onChange={(e) => handleBeneficiaryTypeChange(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Tous</em>
                          </MenuItem>
                          <MenuItem value="fournisseur">Fournisseur</MenuItem>
                          <MenuItem value="beneficiaire">Bénéficiaire</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Step 2: Select Specific Entity (only shown when type is selected) */}
                      {filters.beneficiaire_type && (
                        <Autocomplete
                          size="small"
                          options={getBeneficiaryOptions()}
                          getOptionLabel={(option) => option.label}
                          value={getBeneficiaryOptions().find(o => o.value === getCurrentBeneficiaryValue()) || null}
                          onChange={(_, newValue) => handleBeneficiarySelect(newValue?.value || '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={filters.beneficiaire_type === 'fournisseur' ? 'Fournisseur' : 'Bénéficiaire'}
                              placeholder="Rechercher..."
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                        />
                      )}
                    </Box>
                  </Popover>
                </Box>
              </TableCell>
              <TableCell>Motif</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Libellé
                  <ColumnFilter
                    type="text"
                    value={filters.libelle}
                    onChange={(val) => update('libelle', val)}
                    label="Libellé"
                    placeholder="Rechercher libellé..."
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Pièce Justif.
                  <ColumnFilter
                    type="text"
                    value={filters.piece_justificatif}
                    onChange={(val) => update('piece_justificatif', val)}
                    label="Pièce Justif."
                    placeholder="Rechercher..."
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Mode Paiement
                  <ColumnFilter
                    type="select"
                    value={filters.mode_paiement_id}
                    onChange={(val) => update('mode_paiement_id', val)}
                    options={modesPaiement.map(m => ({ value: m.id.toString(), label: m.nom }))}
                    label="Mode Paiement"
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Réf. Paiement
                  <ColumnFilter
                    type="text"
                    value={filters.reference_paiement}
                    onChange={(val) => update('reference_paiement', val)}
                    label="Réf. Paiement"
                    placeholder="Rechercher..."
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Montant
                  <ColumnFilter
                    type="numberRange"
                    value={filters.montant}
                    onChange={(val) => update('montant', val)}
                    label="Montant"
                    color={FINANCIER_COLOR}
                  />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tabValue === 0 ? decaissementsList : deletedList).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{new Date(item.date).toLocaleDateString('fr-FR')}</TableCell>
                <TableCell>{item.date_echeance ? new Date(item.date_echeance).toLocaleDateString('fr-FR') : '-'}</TableCell>
                <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={item.projet_nom || ''} arrow placement="top">
                    <span>{item.projet_nom || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 150 }}>
                  <Tooltip title={`${getBeneficiaryName(item)} (${BENEFICIAIRE_TYPE_LABELS[item.beneficiaire_type] || item.beneficiaire_type})`} arrow placement="top">
                    <Box>
                      <Typography variant="body2" noWrap>{getBeneficiaryName(item)}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {BENEFICIAIRE_TYPE_LABELS[item.beneficiaire_type] || item.beneficiaire_type}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>{MOTIF_LABELS[item.motif] || item.motif}</TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={item.libelle || ''} arrow placement="top">
                    <span>{item.libelle || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={item.piece_justificatif || ''} arrow placement="top">
                    <span>{item.piece_justificatif || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={item.mode_paiement_nom || ''} arrow placement="top">
                    <span>{item.mode_paiement_nom || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={item.reference_paiement || ''} arrow placement="top">
                    <span>{item.reference_paiement || '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{formatAmount(item.montant_total)}</TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {tabValue === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="Voir détails">
                        <IconButton size="small" onClick={() => handlePreview(item)} color="info"><ViewIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => handleEdit(item)} color="primary"><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" onClick={() => handleDelete(item)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title="Restaurer">
                      <IconButton size="small" onClick={() => handleRestore(item)} color="success"><RestoreIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(tabValue === 0 ? decaissementsList : deletedList).length === 0 && (
              <TableRow><TableCell colSpan={11} align="center">Aucun décaissement trouvé</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tabValue === 0 && (
        <CustomTablePagination
          count={totalCount} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          Détails du Décaissement - {previewItem?.reference}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {previewItem && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography variant="body1">{new Date(previewItem.date).toLocaleDateString('fr-FR')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Référence</Typography>
                <Typography variant="body1">{previewItem.reference}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Projet</Typography>
                <Typography variant="body1">{previewItem.projet_nom || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                <Chip label={STATUS_LABELS[previewItem.status] || previewItem.status} size="small" color={getStatusColor(previewItem.status)} />
              </Grid>

              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Type de Bénéficiaire</Typography>
                <Typography variant="body1">{BENEFICIAIRE_TYPE_LABELS[previewItem.beneficiaire_type] || previewItem.beneficiaire_type}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Bénéficiaire</Typography>
                <Typography variant="body1">{getBeneficiaryName(previewItem)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Motif</Typography>
                <Typography variant="body1">{MOTIF_LABELS[previewItem.motif] || previewItem.motif}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Pièce Justificatif</Typography>
                <Typography variant="body1">{previewItem.piece_justificatif || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Libellé</Typography>
                <Typography variant="body1">{previewItem.libelle || '-'}</Typography>
              </Grid>

              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Type de Paiement</Typography>
                <Typography variant="body1">{PAIEMENT_TYPE_LABELS[previewItem.paiement_type] || previewItem.paiement_type}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Mode de Paiement</Typography>
                <Typography variant="body1">{previewItem.mode_paiement_nom || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Référence Paiement</Typography>
                <Typography variant="body1">{previewItem.reference_paiement || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Date d'Échéance</Typography>
                <Typography variant="body1">{previewItem.date_echeance ? new Date(previewItem.date_echeance).toLocaleDateString('fr-FR') : '-'}</Typography>
              </Grid>

              <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">Montant Total</Typography>
                <Typography variant="h6" color="primary">{formatAmount(previewItem.montant_total)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">Montant Payé</Typography>
                <Typography variant="h6" color="success.main">{formatAmount(previewItem.montant_paye)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">Reste</Typography>
                <Typography variant="h6" color={previewItem.reste > 0 ? 'error.main' : 'success.main'}>
                  {formatAmount(previewItem.reste)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fermer</Button>
          <Button variant="contained" onClick={() => { setPreviewOpen(false); if (previewItem) handleEdit(previewItem); }}>
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      <DecaissementFormDialog open={formDialogOpen} onClose={() => { setFormDialogOpen(false); setFormMinimized(false); }} onSuccess={handleFormSuccess} editId={editId} minimized={formMinimized} onMinimizedChange={setFormMinimized} />
      <AlertDialog {...alertState} onClose={hideAlert} onConfirm={alertState.onConfirm || hideAlert} />
    </Box>
  );
};

export default DecaissementList;
